import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, Hourglass, ShoppingBag, Mail, Trash2, Phone, Scissors, Bath, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import devApi from "../devApi";
import { Skeleton, spring } from "../bits";
import { DevScreen, DevCard, DevList, Tgl, Field, DevInput, SaveBtn } from "./bits";
import ProductEditor from "./ProductEditor";

// ---------- Overview ----------
export const DevOverview = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      devApi.get("/admin/grooming-requests"),
      devApi.get("/admin/selfwash-bookings"),
      devApi.get("/admin/shop/orders"),
      devApi.get("/admin/contact-messages"),
      devApi.get("/admin/shop/stats"),
    ]).then(([g, s, o, msg, st]) => {
      const today = new Date().toISOString().slice(0, 10);
      const groom = g.status === "fulfilled" ? g.value.data : [];
      const selfw = s.status === "fulfilled" ? s.value.data : [];
      const orders = o.status === "fulfilled" ? o.value.data : [];
      const msgs = msg.status === "fulfilled" ? msg.value.data : [];
      setData({
        todayCount: groom.filter((b) => b.preferred_date === today && b.status !== "cancelled").length
          + selfw.filter((b) => b.date === today && b.status !== "cancelled").length,
        pending: groom.filter((b) => b.status === "pending").length + selfw.filter((b) => b.status === "pending").length,
        newOrders: orders.filter((x) => x.status === "new").length,
        unread: msgs.filter((x) => !x.is_read).length,
        revenue: st.status === "fulfilled" ? (st.value.data.revenue ?? st.value.data.total_revenue ?? null) : null,
      });
    });
  }, []);

  const tiles = data ? [
    { icon: CalendarClock, label: m.stat_today, value: data.todayCount },
    { icon: Hourglass, label: m.stat_pending, value: data.pending },
    { icon: ShoppingBag, label: m.stat_orders, value: data.newOrders },
    { icon: Mail, label: m.stat_msgs, value: data.unread },
  ] : [];

  return (
    <DevScreen title={m.sec_overview} onBack={onBack} testid="dev-screen-overview">
      {data === null ? (
        <div className="grid grid-cols-2 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <DevList>
          <div className="grid grid-cols-2 gap-3">
            {tiles.map((tl, i) => (
              <motion.div
                key={tl.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ ...spring, delay: i * 0.07 }}
                className="rounded-2xl bg-white border border-brand/10 p-4 shadow-[0_6px_24px_rgba(134,77,99,0.07)]"
                data-testid={`dev-stat-${tl.label}`}
              >
                <div className="grid place-items-center h-10 w-10 rounded-xl bg-brand-soft text-brand"><tl.icon className="h-5 w-5" /></div>
                <p className="mt-2 font-heading text-3xl font-bold text-brand-ink">{tl.value}</p>
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/40">{tl.label}</p>
              </motion.div>
            ))}
          </div>
          {data.revenue !== null && (
            <DevCard>
              <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/40">{m.stat_revenue}</p>
              <p className="font-heading text-2xl font-bold text-brand mt-1">€{Number(data.revenue).toFixed(2)}</p>
            </DevCard>
          )}
        </DevList>
      )}
    </DevScreen>
  );
};

// ---------- Bookings (grooming / selfwash) ----------
const STATUS_ORDER = ["pending", "confirmed", "done", "cancelled", "no_show"];

export const DevBookings = ({ kind, onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const statuses = t.admin.statuses;
  const isGroom = kind === "grooming";
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    devApi.get(isGroom ? "/admin/grooming-requests" : "/admin/selfwash-bookings")
      .then((r) => setItems(r.data))
      .catch(() => toast.error(m.error_generic));
  }, [isGroom, m.error_generic]);
  useEffect(load, [load]);

  const setStatus = async (b, status) => {
    try {
      await devApi.patch(`${isGroom ? "/admin/grooming-requests" : "/admin/selfwash-bookings"}/${b.id}`, { status });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const del = async (b) => {
    if (!window.confirm(m.dev_del_confirm)) return;
    try {
      await devApi.delete(`${isGroom ? "/admin/grooming-requests" : "/admin/selfwash-bookings"}/${b.id}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const rows = (items || [])
    .map((b) => ({ ...b, _date: isGroom ? b.preferred_date : b.date, _time: isGroom ? b.preferred_time : b.time }))
    .sort((a, b) => `${a._date} ${a._time}`.localeCompare(`${b._date} ${b._time}`));

  return (
    <DevScreen title={isGroom ? m.sec_grooming : m.sec_selfwash} onBack={onBack} testid={`dev-screen-${kind}`}>
      {items === null ? (
        <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-10 text-center">{m.dev_empty}</p>
      ) : (
        <DevList>
          {rows.map((b) => (
            <DevCard key={b.id} >
              <div className="flex items-center gap-3" data-testid={`dev-booking-${b.id}`}>
                <div className={`grid place-items-center h-10 w-10 rounded-xl shrink-0 ${isGroom ? "bg-brand-soft text-brand" : "bg-sky-50 text-sky-600"}`}>
                  {isGroom ? <Scissors className="h-5 w-5" /> : <Bath className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-brand-ink text-sm truncate">{b.customer_name}</p>
                  <p className="text-[11px] text-brand-ink/55 truncate">
                    {b._date} · {b._time}{isGroom ? ` · ${b.pet_name || "—"}` : ` · ${b.plan}${b.nagayu ? " +Nagayu" : ""}`}
                  </p>
                </div>
                <button type="button" onClick={() => del(b)} className="grid place-items-center h-8 w-8 rounded-full text-rose-400 hover:bg-rose-50 shrink-0" data-testid={`dev-booking-del-${b.id}`} aria-label="delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2.5 flex items-center gap-2">
                <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-brand bg-brand-soft/60 rounded-full px-2.5 py-1 no-a11y-underline">
                  <Phone className="h-3 w-3" /> {b.phone}
                </a>
                <div className="ml-auto flex gap-1 flex-wrap justify-end">
                  {STATUS_ORDER.filter((st) => st !== b.status).slice(0, 3).map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => setStatus(b, st)}
                      className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-stone-100 text-brand-ink/55"
                      data-testid={`dev-booking-${b.id}-${st}`}
                    >
                      {statuses[st] || st}
                    </button>
                  ))}
                </div>
              </div>
              <p className="mt-1.5 text-[10px] font-bold uppercase text-brand-ink/35">{statuses[b.status] || b.status}</p>
            </DevCard>
          ))}
        </DevList>
      )}
    </DevScreen>
  );
};

// ---------- Boutique products ----------
const PRODUCT_KEYS = ["name_gr", "name_en", "description_gr", "description_en", "price", "brand", "category_id", "supplier_id", "sizes", "colors", "custom_axis_name_gr", "custom_axis_name_en", "custom_axis_options", "variants", "active", "online_orderable"];

export const DevBoutique = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(undefined); // undefined = closed, null = new, obj = edit

  const load = useCallback(() => {
    devApi.get("/admin/shop/products").then((r) => setProducts(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const toggle = async (p) => {
    try {
      const body = {};
      PRODUCT_KEYS.forEach((k) => { body[k] = p[k] ?? (k === "price" ? 0 : undefined); });
      body.active = !p.active;
      await devApi.put(`/admin/shop/products/${p.id}`, body);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const list = (products || []).filter((p) => !q.trim() || (p.name_gr || "").toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <DevScreen title={m.sec_boutique} onBack={onBack} testid="dev-screen-boutique">
      <motion.button whileTap={{ scale: 0.97 }} onClick={() => setEdit(null)} className="mb-3 w-full h-12 rounded-2xl bg-brand text-brand-cream font-bold flex items-center justify-center gap-2" data-testid="dev-product-new">
        <Plus className="h-5 w-5" /> {m.pe_new}
      </motion.button>
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={m.w_search_ph} className="w-full h-11 rounded-xl border border-brand/20 bg-white pl-11 pr-4 text-sm text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="dev-shop-search" />
      </div>
      {products === null ? (
        <div className="space-y-3"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
      ) : list.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-10 text-center">{m.dev_empty}</p>
      ) : (
        <DevList>
          {list.map((p) => (
            <DevCard key={p.id}>
              <div className="flex items-center gap-3" data-testid={`dev-product-${p.id}`}>
                <button type="button" onClick={() => setEdit(p)} className="min-w-0 flex-1 text-left" data-testid={`dev-product-edit-${p.id}`}>
                  <p className={`font-bold text-sm ${p.active ? "text-brand-ink" : "text-brand-ink/40 line-through"}`}>{p.name_gr}</p>
                  <p className="text-[11px] text-brand-ink/55">€{Number(p.price || 0).toFixed(2)}{(p.variants || []).length ? ` · ${(p.variants || []).length} var.` : ""} · {(p.images || []).length} 📷</p>
                </button>
                <Tgl on={!!p.active} onChange={() => toggle(p)} testid={`dev-product-active-${p.id}`} />
              </div>
            </DevCard>
          ))}
        </DevList>
      )}

      {edit !== undefined && (
        <ProductEditor product={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
      )}
    </DevScreen>
  );
};
